import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import PropTypes from 'prop-types';
import { toast } from '../components/notifications';
import { isDesktop, isMobileBrowser, isNativeApp } from '../utils/deviceDetection';
import { useAuth } from '../contexts/AuthContext';
import ChatService from '../services/ChatService';

// Sub-Komponenten
import AdvancedSearch from './AdvancedSearch';
import ChatOverview from './ChatOverview';
import SessionStatus from './SessionStatus';
import LearningJournalPageV2 from '../pages/LearningJournalPageV2';
import SessionsPage from '../pages/SessionsPage';
import Navigation from './ui/Navigation';
import AuraLightBackground from './ui/AuraLightBackground';
import HomeView from './HomeView';
import MatchRoundView from './MatchRoundView';
import DiscoverHeader, { discoverPanelId, discoverTabId } from './discover/DiscoverHeader.jsx';
import SkillBrowseView from './discover/SkillBrowseView.jsx';
import SessionBannerManager, { useShouldShowBanner } from './SessionBannerManager';
import DraftFeedbackBanner from './DraftFeedbackBanner';
import MainScreenHeader from './MainScreenHeader';

// Custom Hooks
import { useProfileLoader } from '../hooks/useProfileLoader';
import { useMatchRound } from '../hooks/useMatchRound';
import { useMainScreenSessions } from '../hooks/useMainScreenSessions';
import { useInfiniteScroll } from '../hooks/useInfiniteScroll';
import { useAppMotion } from '../hooks/useAppMotion';

const isDev = process.env.NODE_ENV === 'development';
const debugLog = isDev ? console.log.bind(console) : () => { };

// Stabile Referenz statt Inline-Objekt, damit Re-Renders keine neuen Style-Objekte erzeugen
const OVERSCROLL_CONTAIN_STYLE = { overscrollBehavior: 'none' };

// Runden-Ansicht statt Endlosliste (Aug 2026).
//
// Auf `false` zurueckstellen und die alte Liste ist wieder aktiv — HomeView.js
// und useProfileLoader bleiben dafuer unangetastet liegen. Ein vollstaendiges
// MainScreenV2 haette 438 Zeilen View-Switcher dupliziert, von denen sich nichts
// aendert; dieser Schalter gibt denselben Rueckweg an genau einer Stelle.
const USE_MATCH_ROUNDS = true;

// Reiter der Startseite (Skills entdecken, Phase 3): "Personen" ist die Runde,
// "Skills" der Katalog. Der gewählte Reiter überlebt Ansichtswechsel und
// Neuladen im selben Tab, nicht aber einen neuen Start (dann wieder Personen).
const DISCOVER_TAB_KEY = 'eo:discoverTab';
const DISCOVER_ID = 'discover';

const readDiscoverTab = () => {
  try {
    return window.sessionStorage.getItem(DISCOVER_TAB_KEY) === 'skills' ? 'skills' : 'people';
  } catch {
    return 'people';
  }
};

const writeDiscoverTab = (tab) => {
  try {
    window.sessionStorage.setItem(DISCOVER_TAB_KEY, tab);
  } catch {
    // Privater Modus / gesperrter Speicher: dann eben ohne Merken.
  }
};

const MainScreenInner = ({
  initialProfiles
}) => {
  const { user: authUser, isLtiUser } = useAuth();
  const userProfile = authUser;
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [currentView, setCurrentView] = useState('home');
  const [bannerHeight, setBannerHeight] = useState(0);
  const [discoverTab, setDiscoverTab] = useState(readDiscoverTab);
  const [skillQuery, setSkillQuery] = useState('');
  const [skillSearchOpen, setSkillSearchOpen] = useState(false);
  // Suchbegriff, mit dem die Personensuche öffnet ("Personen mit „…“ suchen")
  const [searchSeed, setSearchSeed] = useState('');
  const { page: motionPage } = useAppMotion();

  const isMobile = isMobileBrowser();
  const isNative = isNativeApp();
  const isDesktopView = isDesktop();

  // --- Custom Hooks ---

  const {
    potentialMatches,
    schoolProfiles,
    searchResults,
    isLoading,
    error,
    hasMoreProfiles,
    isLoadingMore,
    totalProfileCount,
    handleLoadMore,
    triggerRefresh,
    canTriggerRefresh,
    handleSearchResults,
    addRatedProfile,
    setIsLoading
  } = useProfileLoader({
    authUser,
    initialProfiles,
    // Bei aktiver Runde laedt useMatchRound die Karten. useProfileLoader bleibt
    // trotzdem eingehaengt — die Suche braucht searchResults, schoolProfiles und
    // handleSearchResults. Beide Hooks bedingt aufzurufen waere ein Verstoss
    // gegen die Rules of Hooks, deshalb dieser Schalter statt einer Verzweigung.
    loadMatchesOnMount: !USE_MATCH_ROUNDS
  });

  const {
    profiles: roundProfiles,
    round,
    meta: roundMeta,
    isLoading: isRoundLoading,
    isDrawing: isRoundDrawing,
    error: roundError,
    loadRound,
    drawNextRound,
    snoozeProfile,
    removeProfile
  } = useMatchRound({ authUser, enabled: USE_MATCH_ROUNDS });

  const {
    activeSession,
    activeSessions,
    plannedSessions,
    completedSessions,
    dismissedSessionId,
    hasSessionErrors,
    sessionsError,
    handleJoinSession,
    handleStartSession,
    handleEndSession,
    cleanupInvalidSessions,
    setDismissedSessionId,
    fetchSessions,
  } = useMainScreenSessions({ authUser, navigate, setCurrentView });

  const shouldShowBanner = useShouldShowBanner(activeSession, currentView, dismissedSessionId);

  // Kein Pull-to-Refresh mehr (September 2026): Die Runden-Ansicht hat den Knopf
  // "Neu mischen" auf der Schlusskarte, die Alt-Endlosliste behaelt den
  // Desktop-Wheel-Refresh aus useInfiniteScroll.
  useInfiniteScroll({
    // Die Runde hat sechs Karten und ein Ende — es gibt nichts nachzuladen.
    enabled: !USE_MATCH_ROUNDS && currentView === 'home',
    onLoadMore: handleLoadMore,
    onDesktopRefresh: () => triggerRefresh('scroll'),
    canRefresh: canTriggerRefresh,
    isDesktop: isDesktopView
  });

  // --- Deep-Link Navigation ---

  // Sessions auffrischen wenn der User zur Home-Ansicht zurückkehrt
  useEffect(() => {
    if (currentView === 'home' && authUser?.schoolId) {
      fetchSessions();
    }
  }, [currentView]); // eslint-disable-line react-hooks/exhaustive-deps

  // Primitive ableiten: searchParams ist bei jedem Router-Update eine neue
  // Objekt-Referenz und wuerde den Effect unnoetig triggern
  const viewParam = searchParams.get('view');
  const chatIdParam = searchParams.get('chatId');

  useEffect(() => {
    if (viewParam === 'chat' && chatIdParam) {
      setSearchParams({}, { replace: true });
      navigate(`/chat/${chatIdParam}`, { replace: true });
    } else if (viewParam === 'chat-overview') {
      setCurrentView('chat-overview');
      setSearchParams({}, { replace: true });
    } else if (viewParam === 'journal') {
      setCurrentView('journal');
      setSearchParams({}, { replace: true });
    } else if (viewParam === 'sessions') {
      setCurrentView('sessions');
      setSearchParams({}, { replace: true });
    }
  }, [viewParam, chatIdParam, setSearchParams, navigate]);

  // Session-View ohne aktive Session -> zurueck zur Home-View
  // (als Effect statt setState waehrend der Render-Phase)
  useEffect(() => {
    if (currentView === 'session' && !activeSession) {
      setCurrentView('home');
    }
  }, [currentView, activeSession]);

  // --- Stabile Callbacks ---

  const handleConnect = useCallback(async (profileId, customMessage = null) => {
    addRatedProfile(profileId);

    // Aus allen Quellen suchen, in denen das Profil liegen kann. Vorher stand
    // hier nur schoolProfiles — seit die Runden-Ansicht aktiv ist, befuellt
    // useProfileLoader die aber nicht mehr, und der Aufruf aus der Suche lief
    // ins Leere ("Profil nicht gefunden"). searchResults ist ohnehin die
    // naheliegendste Quelle fuer einen Klick aus den Suchergebnissen.
    const allProfiles = [
      ...searchResults,
      ...roundProfiles,
      ...schoolProfiles,
      ...(initialProfiles || [])
    ];
    const matchedProfile = allProfiles.find(p => p.id === profileId);

    if (!matchedProfile) {
      console.error("Profil nicht gefunden:", profileId);
      toast.error("Profil konnte nicht gefunden werden.");
      return;
    }

    try {
      setIsLoading(true);

      const message = customMessage ||
        `Hallo ${matchedProfile.name}, ich würde gerne mit dir zu "${matchedProfile.matchedSeeks?.[0] || 'gemeinsamen Themen'}" lernen! Können wir uns connecten?`;

      await ChatService.sendChatRequest(profileId, message);
      debugLog(`Erfolgreich: Chat-Anfrage an ${matchedProfile.name} gesendet`);
    } catch (connectError) {
      console.error("Fehler beim Senden der Chat-Anfrage:", connectError);
      // Bis Okt 2026 zeigte development hier im FEHLERfall einen Erfolgs-Toast
      // ("wurde gesendet"). Ein Fehler ist in jeder Umgebung ein Fehler.
      toast.error(`Anfrage konnte nicht gesendet werden: ${connectError.message}`);
    } finally {
      setIsLoading(false);
    }
  }, [searchResults, roundProfiles, schoolProfiles, initialProfiles, addRatedProfile, setIsLoading]);

  const handleReportMessage = useCallback(async (reportData) => {
    try {
      await ChatService.reportMessage(reportData.messageId, {
        reason: reportData.reason,
        details: reportData.details,
        chatId: reportData.chatId,
        reportedUserId: reportData.sender,
      });
      toast.success('Nachricht wurde gemeldet', { position: 'top-right', autoClose: 3000 });
    } catch (reportError) {
      console.error('Fehler beim Melden:', reportError);
      toast.error('Meldung fehlgeschlagen', { position: 'top-right', autoClose: 3000 });
    }
  }, []);

  const handleBannerHeightChange = useCallback((height) => {
    setBannerHeight(height);
  }, []);

  const handleBannerDismiss = useCallback(() => {
    if (activeSession?.id) {
      setDismissedSessionId(activeSession.id);
    }
  }, [activeSession?.id, setDismissedSessionId]);

  const handleOpenSearch = useCallback(() => {
    setSearchSeed('');
    setCurrentView('search');
  }, []);

  const handleDiscoverTabChange = useCallback((tab) => {
    setDiscoverTab(tab);
    writeDiscoverTab(tab);
  }, []);

  // Die Lupe der Kopfzeile: im Reiter Personen die Suche wie bisher, im Reiter
  // Skills klappt sie das Filterfeld auf (noch einmal getippt: zu und leer).
  const handleDiscoverSearch = useCallback(() => {
    if (discoverTab === 'skills') {
      if (skillSearchOpen) setSkillQuery('');
      setSkillSearchOpen(!skillSearchOpen);
      return;
    }
    handleOpenSearch();
  }, [discoverTab, skillSearchOpen, handleOpenSearch]);

  const handleSearchPeople = useCallback((term) => {
    setSearchSeed(term || '');
    setCurrentView('search');
  }, []);

  const handleEditProfile = useCallback(() => {
    navigate('/profile-edit');
  }, [navigate]);

  const handleSessionClick = useCallback(() => {
    if (activeSession) {
      // Nur echte Solo-Focus-Sessions (PRIVATE + isFocusMode) gehen zur FocusSessionPage.
      // 1:1-PRIVATE-Hosts gehen zur SessionDetailPage (dort läuft die Partner-Validierung).
      const isFocus = activeSession.type === 'PRIVATE' && activeSession.isFocusMode === true;
      navigate(isFocus ? `/focus-session/${activeSession.id}` : `/session/${activeSession.id}`);
    }
  }, [activeSession, navigate]);

  const handleStartSessionNav = useCallback(() => {
    navigate('/create-session');
  }, [navigate]);

  // --- Render Content ---

  const renderContent = () => {
    if (currentView === 'session') {
      if (!activeSession) {
        return null;
      }
      return (
        <SessionStatus
          session={activeSession}
          onClose={() => setCurrentView('home')}
          onEndSession={handleEndSession}
          currentUser={userProfile}
        />
      );
    }

    if (currentView === 'search') {
      return (
        <div className="flex-1">
          <AdvancedSearch
            profiles={schoolProfiles}
            onSearchResults={handleSearchResults}
            sessions={[...activeSessions, ...plannedSessions]}
            currentUser={userProfile}
            onJoinSession={handleJoinSession}
            onConnect={handleConnect}
            initialSearchTerm={searchSeed}
            autoFocus
          />
        </div>
      );
    }

    if (currentView === 'journal') {
      return <LearningJournalPageV2 embedded />;
    }

    if (currentView === 'sessions') {
      return (
        <SessionsPage
          embedded
          currentUser={userProfile}
          onJoinSession={handleJoinSession}
          onValidateCode={handleJoinSession}
        />
      );
    }

    if (currentView === 'chat-overview') {
      return (
        <ChatOverview
          currentUser={userProfile}
          onBack={() => setCurrentView('home')}
          onSelectChat={(chat) => navigate(`/chat/${chat.chatId}`)}
        />
      );
    }

    // Default: Home View — Runde oder (bei USE_MATCH_ROUNDS = false) die alte Liste
    if (USE_MATCH_ROUNDS) {
      const peopleActive = discoverTab === 'people';
      // Beide Reiter bleiben eingehängt und werden nur verdeckt: Wer aus dem
      // Katalog zurückkommt, steht wieder auf derselben Karte der Runde. Die
      // Runde misst ihre Höhe beim Zurückwechseln neu (`active`).
      return (
        <div className="min-w-0">
          <DiscoverHeader
            tab={discoverTab}
            onTabChange={handleDiscoverTabChange}
            onSearch={handleDiscoverSearch}
            searchExpanded={skillSearchOpen}
            idPrefix={DISCOVER_ID}
            className="mb-2"
          />
          <div
            role="tabpanel"
            id={discoverPanelId(DISCOVER_ID, 'people')}
            aria-labelledby={discoverTabId(DISCOVER_ID, 'people')}
            hidden={!peopleActive}
            className="discover-panel"
          >
            <MatchRoundView
              profiles={roundProfiles}
              round={round}
              meta={roundMeta}
              isLoading={isRoundLoading}
              isDrawing={isRoundDrawing}
              error={roundError}
              userProfile={userProfile}
              bannerHeight={shouldShowBanner ? bannerHeight : 0}
              onDraw={drawNextRound}
              onSnooze={snoozeProfile}
              onBlocked={removeProfile}
              onOpenSearch={handleOpenSearch}
              onRetry={loadRound}
              active={peopleActive}
            />
          </div>
          <div
            role="tabpanel"
            id={discoverPanelId(DISCOVER_ID, 'skills')}
            aria-labelledby={discoverTabId(DISCOVER_ID, 'skills')}
            hidden={peopleActive}
            className="discover-panel"
          >
            {/* Lädt erst, wenn der Reiter offen ist, und meldet BROWSE_TAB_OPENED
                selbst (einmal je Öffnung), deshalb hier kein eigenes Ereignis. */}
            <SkillBrowseView
              enabled={!peopleActive}
              query={skillQuery}
              onQueryChange={setSkillQuery}
              searchOpen={skillSearchOpen}
              onSearchOpenChange={setSkillSearchOpen}
              onSearchPeople={handleSearchPeople}
              onEditProfile={handleEditProfile}
            />
          </div>
        </div>
      );
    }

    return (
      <HomeView
        potentialMatches={potentialMatches}
        totalProfileCount={totalProfileCount}
        isLoading={isLoading}
        error={error}
        isLoadingMore={isLoadingMore}
        hasMoreProfiles={hasMoreProfiles}
        userProfile={userProfile}
        onConnect={handleConnect}
        onLoadMore={handleLoadMore}
        onOpenSearch={handleOpenSearch}
      />
    );
  };

  const shouldFixHeader = currentView !== 'session';

  // Die Bannerhoehe als CSS-Variable nach unten durchreichen: die Match-Karte
  // muss sie von ihrer eigenen Hoehe abziehen, sonst schiebt ein eingeblendetes
  // Session-Banner ihren Fuss (und damit den Vernetzen-Knopf) unter die
  // Navigationsleiste. Der Wert liegt hier ohnehin schon vor.
  const containerStyle = useMemo(() => ({
    ...((isMobile || isNative) ? OVERSCROLL_CONTAIN_STYLE : {}),
    '--banner-height': shouldShowBanner ? `${bannerHeight}px` : '0px'
  }), [isMobile, isNative, shouldShowBanner, bannerHeight]);

  const mainPaddingStyle = useMemo(() => (
    shouldFixHeader ? {
      paddingTop: shouldShowBanner
        ? `calc(var(--mobile-header-height) + ${bannerHeight}px + 20px)`
        : 'calc(var(--mobile-header-height) + 20px)'
    } : undefined
  ), [shouldFixHeader, shouldShowBanner, bannerHeight]);

  return (
    <div
      // data-nav-rail: Ab 600px steht die Navigation als Spalte links (siehe
      // index.css, "Die Tablet-Schwelle"). Das Attribut begrenzt die Regeln
      // dafuer — Token-Rueckfall, Einrueckung von Kopf und Inhalt — auf diesen
      // Baum. Das Dashboard hat keine Spalte und darf davon nichts erben.
      data-nav-rail=""
      className="min-h-screen relative overflow-x-clip"
      style={containerStyle}
    >
      <AuraLightBackground intensity="normal" />

      <SessionBannerManager
        activeSession={activeSession}
        dismissedSessionId={dismissedSessionId}
        currentView={currentView}
        onDismiss={handleBannerDismiss}
        onBannerHeightChange={handleBannerHeightChange}
      />

      <MainScreenHeader
        userProfile={userProfile}
        isLtiUser={isLtiUser}
        currentView={currentView}
        activeSessions={activeSessions}
        plannedSessions={plannedSessions}
        completedSessions={completedSessions}
        shouldShowBanner={shouldShowBanner}
        bannerHeight={bannerHeight}
        hasSessionErrors={hasSessionErrors}
        onJoinSession={handleJoinSession}
        onSetCurrentView={setCurrentView}
        onCleanupSessions={cleanupInvalidSessions}
      />

      <main
        // data-app-main ist der Haken fuer eine Regel in index.css: Ab 600px
        // haelt der Inhalt links den Platz fuer die seitliche Navigation frei.
        // Warum dort und nicht als Tailwind-Klasse: `sm:px-4` greift ab 640px
        // und wuerde ein `pl-*` gleicher Spezifitaet ueberschreiben.
        data-app-main
        // px-4 wie PageShell-Unterseiten (Okt 2026: beim Wechsel Tab -> Detail
        // sprang der Inhalt um 4 px). Unten der Seiten-Token, nicht der Rohwert
        // der Leiste: --page-bottom-nav setzt auf --bottom-nav-clearance auf.
        className={`relative z-10 ${currentView === 'session' ? 'p-0' : 'px-4 pt-4 pb-[var(--page-bottom-nav)]'
          }`}
        style={mainPaddingStyle}
      >
        {/* max-w-6xl ist dieselbe Breite, die PageShell als Rolle `list` vergibt.
            Vorher stand hier max-w-7xl: Logbuch und Sessions waren dadurch in der
            Hauptansicht 1280px breit und als eigene Route 768 bzw. 1152 — dieselbe
            Ansicht sah je nach Einstiegsweg anders aus. */}
        <div className={currentView === 'session' ? 'h-full' : 'max-w-6xl mx-auto'}>
          {/* Lernlogbuch 2.0: Hinweis auf unvollständige Logbucheinträge (Auto-Complete) */}
          {currentView !== 'session' && <DraftFeedbackBanner />}
          {/* Stale-on-Error: Sessions bleiben sichtbar, Banner bietet Retry an */}
          {currentView !== 'session' && sessionsError && (
            <div className="mb-4 flex items-center justify-between gap-3 rounded-xl border border-warning/40 bg-warning-soft px-4 py-3 text-sm text-foreground animate-fade-in">
              <span>Sessions konnten nicht aktualisiert werden - Anzeige ist evtl. veraltet.</span>
              <button
                type="button"
                onClick={fetchSessions}
                className="shrink-0 font-semibold underline underline-offset-2"
              >
                Erneut versuchen
              </button>
            </div>
          )}
          <AnimatePresence mode="wait" initial={false}>
            {/* Werte kommen aus useAppMotion — dieselben, die hier vorher fest
                standen, jetzt aber an einer Stelle fuer die ganze App. */}
            <motion.div
              key={currentView}
              {...motionPage}
              className={currentView === 'session' ? 'h-full' : undefined}
            >
              {renderContent()}
            </motion.div>
          </AnimatePresence>
        </div>
      </main>

      <Navigation
        currentView={currentView}
        setCurrentView={setCurrentView}
        activeSession={activeSession}
        onSessionClick={handleSessionClick}
        onStartSession={handleStartSessionNav}
      />
    </div>
  );
};

MainScreenInner.propTypes = {
  initialProfiles: PropTypes.array
};

// React.memo verhindert Re-Renders, wenn Eltern-Komponente (App.js)
// re-rendert, aber unsere Props referenz-gleich bleiben.
// Wichtig: dazu MUSS die Eltern-Seite stabile Referenzen liefern
// (initialProfiles/initialChatHistory als Modul-Konstanten).
export const MainScreen = React.memo(MainScreenInner);

export default MainScreen;
